/**
 * AthleteHeaderAvatar (WAVE Visual-v4 P1.1) — canonical app-shell avatar.
 *
 * Canon v4 section header: 26×26 circle, linear gradient bg-3 → bg-4, inset
 * highlight, initials in Onest 700 ink-1. Replaces the «S» brand logo
 * in the authenticated app shell. Brand mark stays on onboarding,
 * landing, and login surfaces only.
 *
 * Click → navigate /profile (canonical drill-down).
 */
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Athlete } from "../auth";

export interface AthleteHeaderAvatarProps {
  me?: Athlete | null;
  /** Override target route. Default `/profile`. */
  to?: string;
}

export function extractInitials(me?: Athlete | null): string {
  if (!me) return "•";
  const first = (me.first_name ?? "").trim();
  const last = (me.last_name ?? "").trim();
  if (first && last) return (first[0] + last[0]).toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  if (last) return last.slice(0, 2).toUpperCase();
  const email = (me.email ?? "").trim();
  if (email) return email.slice(0, 2).toUpperCase();
  return "•";
}

export function AthleteHeaderAvatar({ me, to = "/profile" }: AthleteHeaderAvatarProps) {
  const nav = useNavigate();
  const { t } = useTranslation("common");
  const initials = useMemo(() => extractInitials(me), [me]);

  return (
    <button
      type="button"
      onClick={() => nav(to)}
      aria-label={t("a11y.open_profile", { defaultValue: "Открыть профиль" })}
      data-testid="athlete-header-avatar"
      className="athlete-avatar relative w-[26px] h-[26px] rounded-full flex items-center justify-center text-[10px] font-bold text-fg-primary tracking-[-0.01em] focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base"
    >
      <span className="num-display leading-none">{initials}</span>
    </button>
  );
}
